/**
 * Banking page — banki tranzakciók listája + CSV import + manuális match.
 *
 * A backend `/banking` endpoint-jai mögött. CSV upload közvetlen FormData-ban
 * a `useImportBankCsv` hookkal. A unmatched tranzakciókat amber-rel kiemeljük.
 */
import { useState } from 'react'
import { CreditCard, Upload, Link2, Unlink, FileWarning, ArrowDownRight, ArrowUpRight, FileText, Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card } from '@/components/ui/Card'
import { CenterSpinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { Input, Label } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/ToastProvider'
import { useAuth } from '@/lib/auth/AuthProvider'
import {
  useBankTransactions,
  useImportBankCsv,
  useImportBankPdf,
  useInvoices,
  useMatchBankTransaction,
  useUnmatchBankTransaction,
  useDeleteBankTransaction,
  useDeleteBankTransactions,
  useRematchBankTransactions,
} from '@/lib/api/hooks'
import { dateShort, money } from '@/lib/format'
import type { BankTransaction } from '@/lib/api/types'

interface BulkImportResult {
  files: number
  failed: number
  total: number
  inserted: number
  duplicate: number
  autoMatched: number
  aiEnriched: number
  bankName?: string
}

export function BankingPage() {
  const { activeCompanyId } = useAuth()
  const toast = useToast()
  const [tab, setTab] = useState<'all' | 'unmatched'>('unmatched')
  const txQ = useBankTransactions(activeCompanyId, tab === 'unmatched')
  const importMut = useImportBankCsv()
  const importPdfMut = useImportBankPdf()
  const unmatchMut = useUnmatchBankTransaction()
  const deleteMut = useDeleteBankTransaction()
  const bulkDeleteMut = useDeleteBankTransactions()
  const rematchMut = useRematchBankTransactions()

  const [matchTx, setMatchTx] = useState<BankTransaction | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [pdfUploadOpen, setPdfUploadOpen] = useState(false)
  const [pdfResult, setPdfResult] = useState<BulkImportResult | null>(null)
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number; current: string } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ kind: 'single'; tx: BankTransaction } | { kind: 'bulk' } | null>(null)

  if (!activeCompanyId) {
    return <p className="text-sm text-slate-500">Nincs aktív cég.</p>
  }

  /** Több fájl sorban feldolgozása progress-szel + összesített eredmény. */
  async function runBulk(
    files: File[],
    importOne: (file: File) => Promise<{
      total?: number
      inserted?: number
      duplicate?: number
      autoMatched?: number
      aiEnriched?: number
      bankName?: string
    }>,
  ): Promise<BulkImportResult> {
    const agg: BulkImportResult = {
      files: files.length,
      failed: 0,
      total: 0,
      inserted: 0,
      duplicate: 0,
      autoMatched: 0,
      aiEnriched: 0,
    }
    for (let i = 0; i < files.length; i++) {
      setBulkProgress({ done: i, total: files.length, current: files[i].name })
      try {
        const res = await importOne(files[i])
        agg.total += res.total ?? 0
        agg.inserted += res.inserted ?? 0
        agg.duplicate += res.duplicate ?? 0
        agg.autoMatched += res.autoMatched ?? 0
        agg.aiEnriched += res.aiEnriched ?? 0
        if (!agg.bankName && res.bankName && res.bankName !== 'unknown') agg.bankName = res.bankName
      } catch {
        agg.failed++
      }
    }
    setBulkProgress(null)
    return agg
  }

  async function onCsvUpload(files: File[]) {
    const agg = await runBulk(files, (file) => importMut.mutateAsync({ companyId: activeCompanyId!, file }))
    toast.show({
      tone: agg.failed === files.length ? 'error' : agg.failed > 0 ? 'warning' : 'success',
      title: `${agg.files} fájl · ${agg.inserted} új tranzakció · ${agg.autoMatched} auto-match`,
      description:
        (agg.duplicate > 0 ? `${agg.duplicate} duplikátum kihagyva. ` : '') +
        (agg.failed > 0 ? `${agg.failed} fájl hibás.` : ''),
    })
    if (agg.failed < files.length) setUploadOpen(false)
  }

  async function onPdfUpload(files: File[]) {
    const agg = await runBulk(files, (file) => importPdfMut.mutateAsync({ companyId: activeCompanyId!, file }))
    setPdfResult(agg)
    toast.show({
      tone: agg.total === 0 ? 'warning' : agg.failed > 0 ? 'warning' : 'success',
      title: `${agg.files} fájl · ${agg.inserted} új tranzakció · ${agg.autoMatched} auto-match`,
      description:
        (agg.bankName ? `Bank: ${agg.bankName.toUpperCase()}. ` : '') +
        (agg.duplicate > 0 ? `${agg.duplicate} duplikátum kihagyva. ` : '') +
        (agg.aiEnriched > 0 ? `AI ${agg.aiEnriched} mezőt kitöltött. ` : '') +
        (agg.failed > 0 ? `${agg.failed} fájl hibás.` : ''),
    })
  }

  async function onUnmatch(tx: BankTransaction) {
    try {
      await unmatchMut.mutateAsync(tx.id)
      toast.show({ tone: 'success', title: 'Párosítás visszavonva' })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Hiba', description: err instanceof Error ? err.message : String(err) })
    }
  }

  async function onRematch() {
    try {
      const res = await rematchMut.mutateAsync()
      toast.show({
        tone: res.matched > 0 ? 'success' : 'info',
        title: res.matched > 0 ? `${res.matched} új párosítás` : 'Nincs új párosítás',
        description: `${res.scanned} párosítatlan tétel átnézve.`,
      })
    } catch (err) {
      toast.show({ tone: 'error', title: 'Újrapárosítás hiba', description: err instanceof Error ? err.message : String(err) })
    }
  }

  async function onConfirmDelete() {
    if (!confirmDelete) return
    try {
      if (confirmDelete.kind === 'single') {
        const res = await deleteMut.mutateAsync(confirmDelete.tx.id)
        toast.show({
          tone: 'success',
          title: 'Tranzakció törölve',
          description: res.revertedInvoices > 0 ? `${res.revertedInvoices} számla visszaállítva fizetetlenre.` : '',
        })
      } else {
        const ids = items.map((t) => t.id)
        const res = await bulkDeleteMut.mutateAsync(ids)
        toast.show({
          tone: 'success',
          title: `${res.deleted} tranzakció törölve`,
          description: res.revertedInvoices > 0 ? `${res.revertedInvoices} számla visszaállítva fizetetlenre.` : '',
        })
      }
      setConfirmDelete(null)
    } catch (err) {
      toast.show({ tone: 'error', title: 'Törlés sikertelen', description: err instanceof Error ? err.message : String(err) })
    }
  }

  const items = txQ.data?.items ?? []
  const unmatchedCount = items.filter((t) => !t.matchedInvoiceId).length
  const matchedCount = items.filter((t) => t.matchedInvoiceId).length

  return (
    <div>
      <PageHeader
        accent
        eyebrow="Pénzügyi modul"
        title="Banki tételek"
        subtitle="Tranzakciók importálása PDF kivonatból vagy CSV-ből — automatikus párosítás a számlákkal."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setUploadOpen(true)}
              leftIcon={<Upload className="h-4 w-4" />}
            >
              CSV import
            </Button>
            <Button
              onClick={() => {
                setPdfResult(null)
                setPdfUploadOpen(true)
              }}
              leftIcon={<FileText className="h-4 w-4" />}
            >
              OTP PDF kivonat
            </Button>
          </>
        }
      />

      {/* Tab bar + tömeges törlés */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-md p-1 w-fit">
          <button
            type="button"
            onClick={() => setTab('unmatched')}
            className={`px-3 py-1.5 text-sm rounded ${
              tab === 'unmatched'
                ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            Még nem párosított ({tab === 'unmatched' ? items.length : unmatchedCount})
          </button>
          <button
            type="button"
            onClick={() => setTab('all')}
            className={`px-3 py-1.5 text-sm rounded ${
              tab === 'all'
                ? 'bg-white dark:bg-slate-900 shadow-sm text-slate-900 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            Mind {tab === 'all' && `(${items.length})`}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={onRematch}
            loading={rematchMut.isPending}
            leftIcon={<Link2 className="h-4 w-4" />}
          >
            Újrapárosítás
          </Button>
          {items.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              onClick={() => setConfirmDelete({ kind: 'bulk' })}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              Listázott törlése ({items.length})
            </Button>
          )}
        </div>
      </div>

      {txQ.isLoading ? (
        <CenterSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CreditCard className="h-8 w-8" />}
          title={tab === 'unmatched' ? 'Minden tranzakció párosítva' : 'Még nincs banki tranzakció'}
          description={
            tab === 'unmatched'
              ? 'Vagy nincs még semmi importálva, vagy az auto-matching mindent megtalált.'
              : 'Tölts fel egy banki CSV-t a "CSV import" gombbal.'
          }
        />
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Dátum</th>
                  <th className="text-left px-3 py-2">Partner</th>
                  <th className="text-left px-3 py-2">Közlemény</th>
                  <th className="text-right px-3 py-2">Összeg</th>
                  <th className="text-left px-3 py-2">Párosítás</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {items.map((tx) => {
                  const isIncome = tx.amount > 0
                  return (
                    <tr
                      key={tx.id}
                      className={
                        !tx.matchedInvoiceId
                          ? 'bg-amber-50/40 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                      }
                    >
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                        {dateShort(tx.transactionDate)}
                      </td>
                      <td className="px-3 py-2 max-w-[16rem] truncate">
                        <div className="font-medium text-slate-800 dark:text-slate-200">
                          {tx.partnerName ?? '—'}
                        </div>
                        {tx.partnerAccount && (
                          <div className="text-[10px] font-mono text-slate-500 dark:text-slate-400">
                            {tx.partnerAccount}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 max-w-[20rem] truncate text-slate-600 dark:text-slate-400">
                        {tx.reference ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap">
                        <span
                          className={
                            isIncome ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-800 dark:text-slate-200'
                          }
                        >
                          {isIncome ? (
                            <ArrowDownRight className="inline h-3 w-3 mr-0.5" />
                          ) : (
                            <ArrowUpRight className="inline h-3 w-3 mr-0.5" />
                          )}
                          {money(tx.amount, tx.currency)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {tx.matchedInvoiceId ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="inline-flex items-center gap-1 text-xs">
                              <Link2 className="h-3 w-3 text-emerald-600" />
                              <span className="font-mono">{tx.matchedInvoiceNumber}</span>
                            </span>
                            <div className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                              {tx.matchedSupplierName && <span>{tx.matchedSupplierName}</span>}
                              {tx.matchMethod === 'auto' && (
                                <span className="inline-flex items-center rounded bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 px-1 py-0.5 font-medium">
                                  auto {tx.matchConfidence != null ? `${Math.round(tx.matchConfidence * 100)}%` : ''}
                                </span>
                              )}
                              {tx.matchMethod === 'manual' && (
                                <span className="inline-flex items-center rounded bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200 px-1 py-0.5 font-medium">
                                  manuális
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                            <FileWarning className="h-3 w-3" /> Nincs párosítva
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          {tx.matchedInvoiceId ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onUnmatch(tx)}
                              leftIcon={<Unlink className="h-3 w-3" />}
                            >
                              Levesz
                            </Button>
                          ) : (
                            <Button size="sm" variant="secondary" onClick={() => setMatchTx(tx)}>
                              Párosít
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Tranzakció törlése"
                            onClick={() => setConfirmDelete({ kind: 'single', tx })}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {tab === 'all' && items.length > 0 && (
                <tfoot className="bg-slate-50 dark:bg-slate-800 text-xs text-slate-600 dark:text-slate-400">
                  <tr>
                    <td colSpan={4} className="px-3 py-2">
                      {matchedCount} párosított · {unmatchedCount} nem párosított
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      )}

      {/* CSV upload modal */}
      <CsvUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={onCsvUpload}
        loading={importMut.isPending || bulkProgress !== null}
        progress={bulkProgress}
      />

      {/* PDF (OTP) upload modal */}
      <PdfUploadModal
        open={pdfUploadOpen}
        onClose={() => {
          setPdfUploadOpen(false)
          setPdfResult(null)
        }}
        onSubmit={onPdfUpload}
        loading={importPdfMut.isPending || bulkProgress !== null}
        result={pdfResult}
        progress={bulkProgress}
      />

      {/* Manuális match modal */}
      {matchTx && (
        <ManualMatchModal
          transaction={matchTx}
          companyId={activeCompanyId}
          onClose={() => setMatchTx(null)}
          onMatched={() => {
            setMatchTx(null)
            toast.show({ tone: 'success', title: 'Párosítva' })
          }}
        />
      )}

      {/* Törlés megerősítő modal */}
      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete?.kind === 'bulk' ? 'Listázott tranzakciók törlése' : 'Tranzakció törlése'}
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(null)}>
              Mégse
            </Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              loading={deleteMut.isPending || bulkDeleteMut.isPending}
              onClick={onConfirmDelete}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              Törlés véglegesen
            </Button>
          </>
        }
      >
        {confirmDelete?.kind === 'bulk' ? (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Biztosan törlöd a jelenleg listázott <strong>{items.length}</strong> banki tranzakciót? Ez a művelet
            nem vonható vissza. A hozzájuk párosított számlák — ha más tranzakció nem fedezi őket — visszaállnak{' '}
            <strong>fizetetlen</strong> státuszra.
          </p>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Biztosan törlöd ezt a tranzakciót?
            {confirmDelete?.kind === 'single' && (
              <span className="mt-2 block rounded bg-slate-50 dark:bg-slate-800 px-3 py-2 font-mono text-xs">
                {dateShort(confirmDelete.tx.transactionDate)} · {confirmDelete.tx.partnerName ?? '—'} ·{' '}
                {money(confirmDelete.tx.amount, confirmDelete.tx.currency)}
              </span>
            )}
            <span className="mt-2 block text-xs text-slate-500">
              A művelet nem vonható vissza. Ha számlához volt párosítva, az visszaállhat fizetetlenre.
            </span>
          </p>
        )}
      </Modal>
    </div>
  )
}

function PdfUploadModal({
  open,
  onClose,
  onSubmit,
  loading,
  result,
  progress,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (files: File[]) => Promise<void>
  loading: boolean
  result: BulkImportResult | null
  progress: { done: number; total: number; current: string } | null
}) {
  const [files, setFiles] = useState<File[]>([])
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="OTP bank kivonat (PDF) import"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {result ? 'Bezár' : 'Mégse'}
          </Button>
          <Button
            disabled={files.length === 0}
            loading={loading}
            onClick={() => files.length > 0 && onSubmit(files)}
            leftIcon={<FileText className="h-4 w-4" />}
          >
            {files.length > 1 ? `${files.length} fájl feldolgozása` : 'Feldolgozás indítása'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-md border border-brand-200/60 dark:border-brand-900/40 bg-brand-fade dark:bg-brand-fade-dark px-3 py-2 text-xs text-slate-700 dark:text-slate-200 flex items-start gap-2">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 text-brand-600 dark:text-brand-300 shrink-0" />
          <div>
            Tölts fel <strong>egy vagy több PDF kivonatot</strong> (pl. egy egész év havi kivonatait egyszerre —
            Ctrl/Shift a kijelöléshez). A rendszer mindegyikből kinyeri a tranzakciókat (partner, összeg,
            közlemény — AI segítségével is), kihagyja a duplikátumokat, és párosít a nyitott számlákkal.
          </div>
        </div>

        <div>
          <Label htmlFor="bank-pdf-file">PDF fájl(ok) (egyenként max 15 MB)</Label>
          <input
            id="bank-pdf-file"
            type="file"
            accept=".pdf,application/pdf"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-600 file:text-white file:cursor-pointer hover:file:bg-brand-700"
          />
          {files.length > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              {files.length} fájl kiválasztva · összesen {(files.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB
            </p>
          )}
        </div>

        {/* Folyamatjelző */}
        {progress && (
          <div className="rounded-md border border-brand-200 dark:border-brand-900/40 bg-brand-fade dark:bg-brand-fade-dark px-3 py-2 text-xs">
            <div className="flex items-center justify-between mb-1 text-slate-700 dark:text-slate-200">
              <span>Feldolgozás: {progress.done} / {progress.total}</span>
              <span className="truncate ml-2 max-w-[14rem] text-slate-500">{progress.current}</span>
            </div>
            <div className="h-1.5 rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div
                className="h-full bg-brand-600 transition-all"
                style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Eredmény panel (aggregált) */}
        {result && !progress && (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-3 py-3 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
              <Sparkles className="h-4 w-4 text-accent-600 dark:text-accent-400" />
              <span className="font-medium">
                {result.files} fájl feldolgozva · {result.total} tranzakció
                {result.bankName ? ` (${result.bankName.toUpperCase()})` : ''}
                {result.failed > 0 ? ` · ${result.failed} fájl hibás` : ''}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-slate-500">Új</div>
                <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 num-display">{result.inserted}</div>
              </div>
              <div>
                <div className="text-slate-500">Párosítva</div>
                <div className="text-lg font-semibold text-brand-600 dark:text-brand-300 num-display">{result.autoMatched}</div>
              </div>
              <div>
                <div className="text-slate-500">Duplikátum</div>
                <div className="text-lg font-semibold text-slate-500 num-display">{result.duplicate}</div>
              </div>
              <div>
                <div className="text-slate-500">AI-kitöltött</div>
                <div className="text-lg font-semibold text-accent-600 dark:text-accent-400 num-display">{result.aiEnriched}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function CsvUploadModal({
  open,
  onClose,
  onSubmit,
  loading,
  progress,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (files: File[]) => Promise<void>
  loading: boolean
  progress: { done: number; total: number; current: string } | null
}) {
  const [files, setFiles] = useState<File[]>([])
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Banki CSV import"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Mégse</Button>
          <Button
            disabled={files.length === 0}
            loading={loading}
            onClick={() => files.length > 0 && onSubmit(files)}
          >
            {files.length > 1 ? `${files.length} fájl importálása` : 'Import indítása'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Univerzális CSV parser. A fejlécben felismeri a magyar bankok gyakori oszlop-neveit
          (<code>Dátum</code>, <code>Összeg</code>, <code>Közlemény</code>, <code>Partner</code>, stb.).
          Pozitív összeg = bevétel, negatív = átutalás. <strong>Több fájl egyszerre is feltölthető.</strong>
        </p>
        <div>
          <Label htmlFor="csv-file">CSV fájl(ok) (egyenként max 5 MB)</Label>
          <input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand-600 file:text-white file:cursor-pointer hover:file:bg-brand-700"
          />
          {files.length > 0 && (
            <p className="mt-1 text-xs text-slate-500">{files.length} fájl kiválasztva</p>
          )}
        </div>
        {progress && (
          <div className="rounded-md border border-brand-200 dark:border-brand-900/40 bg-brand-fade dark:bg-brand-fade-dark px-3 py-2 text-xs">
            <div className="flex items-center justify-between mb-1 text-slate-700 dark:text-slate-200">
              <span>Feldolgozás: {progress.done} / {progress.total}</span>
              <span className="truncate ml-2 max-w-[14rem] text-slate-500">{progress.current}</span>
            </div>
            <div className="h-1.5 rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div
                className="h-full bg-brand-600 transition-all"
                style={{ width: `${Math.round((progress.done / Math.max(1, progress.total)) * 100)}%` }}
              />
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Az importálás után automatikus matching is fut — a fizetetlen számlák közül megpróbál
          egyezőt találni (összeg + partner + invoice szám közleményben + dátum tolerance). 70% confidence
          felett auto-megerősítés, alatta manuális megerősítés kell.
        </p>
      </div>
    </Modal>
  )
}

function ManualMatchModal({
  transaction,
  companyId,
  onClose,
  onMatched,
}: {
  transaction: BankTransaction
  companyId: string
  onClose: () => void
  onMatched: () => void
}) {
  const [search, setSearch] = useState('')
  const isPayment = transaction.amount < 0
  const direction = isPayment ? 'incoming' : 'outgoing'
  // Lekérdezzük az unpaid számlákat, és kliens-oldali fuzzy szűrés a search-szel
  const invoicesQ = useInvoices({ companyId, page: 1, pageSize: 200, q: search.trim() || undefined })
  const match = useMatchBankTransaction()

  const candidates = (invoicesQ.data?.items ?? []).filter(
    (i) =>
      i.paymentStatus !== 'paid' &&
      // V1.4: csak az iránynak megfelelő számlákat ajánljuk
      // (a számla típusát direction határozza meg az InvoiceDetail-en, de a list-en csak invoiceListItem-et kapunk —
      //  a `direction` property nincs az InvoiceListItem-en, ezért most NEM szűrünk irány alapján.)
      true,
  )
  void direction

  async function onPick(invoiceId: string) {
    await match.mutateAsync({ transactionId: transaction.id, invoiceId })
    onMatched()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Manuális párosítás"
      footer={<Button variant="secondary" onClick={onClose}>Bezárás</Button>}
    >
      <div className="space-y-3">
        <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-3 text-sm">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-medium">{transaction.partnerName ?? '(ismeretlen partner)'}</span>
            <span
              className={`font-mono tabular-nums ${
                isPayment ? 'text-slate-800 dark:text-slate-200' : 'text-emerald-700 dark:text-emerald-300'
              }`}
            >
              {money(transaction.amount, transaction.currency)}
            </span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {dateShort(transaction.transactionDate)} · {transaction.reference ?? '— közlemény nincs —'}
          </div>
        </div>

        <div>
          <Label htmlFor="match-search">Keresés a számlák közt</Label>
          <Input
            id="match-search"
            placeholder="pl. SZ2026, beszállító, közlemény…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-72 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-700">
          {invoicesQ.isLoading && <div className="p-3 text-xs text-slate-500">Keresés…</div>}
          {!invoicesQ.isLoading && candidates.length === 0 && (
            <div className="p-3 text-xs text-slate-500">Nincs találat.</div>
          )}
          {candidates.slice(0, 30).map((inv) => (
            <button
              key={inv.id}
              type="button"
              onClick={() => onPick(inv.id)}
              disabled={match.isPending}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm flex items-center justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs truncate">{inv.invoiceNumber ?? '—'}</div>
                <div className="text-slate-600 dark:text-slate-300 truncate">{inv.supplierName ?? '—'}</div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="font-semibold tabular-nums">{money(inv.grossAmount, inv.currency)}</div>
                <div className="text-[10px] text-slate-500">{dateShort(inv.issueDate)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  )
}

// Suppress unused param warning
void Select
